import { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API, BACKEND_URL } from "../../App";
import {
  Plus, Upload, Trash2, Film, Play, Eye, EyeOff,
  Clapperboard, Save, X
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogTitle, DialogHeader,
} from "../../components/ui/dialog";

const SDE_TYPES = [
  { value: "groom", label: "Groom" },
  { value: "bride", label: "Bride" },
  { value: "best_man", label: "Best Man" },
  { value: "maid_of_honor", label: "Maid of Honor" },
];

export default function CoupleSDEManager() {
  const { wedding } = useOutletContext();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSlot, setUploadSlot] = useState(null);
  const fileInputRef = useRef(null);
  const [newProject, setNewProject] = useState({
    sde_type: "groom", participant_name: "", transition_type: "fade"
  });

  const fetchProjects = async () => {
    if (!wedding?.wedding_id) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/sde/wedding/${wedding.wedding_id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setProjects(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchProjects(); }, [wedding]);

  const getMediaUrl = (url) => (url?.startsWith("http") ? url : `${BACKEND_URL}${url}`);

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/sde`, { ...newProject, wedding_id: wedding.wedding_id },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("SDE project created!");
      setCreateOpen(false);
      setNewProject({ sde_type: "groom", participant_name: "", transition_type: "fade" });
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create project");
    }
  };

  const handleTogglePublish = async (project) => {
    try {
      const token = localStorage.getItem("token");
      const newStatus = project.status === "published" ? "draft" : "published";
      await axios.put(`${API}/sde/${project.sde_id}`, { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newStatus === "published" ? "SDE published!" : "SDE unpublished");
      fetchProjects();
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (sdeId) => {
    if (!window.confirm("Delete this SDE project?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/sde/${sdeId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted");
      fetchProjects();
      if (selectedProject?.sde_id === sdeId) setSelectedProject(null);
    } catch { toast.error("Failed to delete"); }
  };

  const handleClipUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProject || !uploadSlot) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("slot_number", uploadSlot);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/sde/${selectedProject.sde_id}/clips`, formData,
        { headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` } });
      setSelectedProject(res.data);
      fetchProjects();
      toast.success(`Clip uploaded to slot ${uploadSlot}`);
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); setUploadSlot(null); }
  };

  const handleDeleteClip = async (slotNumber) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/sde/${selectedProject.sde_id}/clips/${slotNumber}`,
        { headers: { Authorization: `Bearer ${token}` } });
      const res = await axios.get(`${API}/sde/${selectedProject.sde_id}`);
      setSelectedProject(res.data);
      fetchProjects();
      toast.success("Clip removed");
    } catch { toast.error("Failed to remove clip"); }
  };

  const triggerUpload = (slot) => {
    setUploadSlot(slot);
    fileInputRef.current?.click();
  };

  const [previewClip, setPreviewClip] = useState(null);

  if (!wedding) return <div className="text-wedding-ivory/60 text-center py-12">No wedding assigned.</div>;

  return (
    <div className="space-y-8" data-testid="couple-sde-manager">
      <input ref={fileInputRef} type="file" accept="video/mp4,video/quicktime" onChange={handleClipUpload} className="hidden" />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-wedding-ivory">Personal SDE</h1>
          <p className="text-wedding-ivory/60 mt-1">Manage Same-Day Edit perspectives</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="btn-primary" data-testid="create-sde-btn">
          <Plus className="w-4 h-4 mr-2" />New SDE
        </Button>
      </div>

      {/* Project List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin" />
        </div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-white/10">
          <Clapperboard className="w-16 h-16 text-wedding-gold/20 mx-auto mb-4" />
          <p className="text-wedding-ivory/50 mb-2">No SDE projects yet</p>
          <p className="text-wedding-ivory/30 text-sm">Create perspectives for your wedding day</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          {projects.map((project, i) => (
            <motion.div
              key={project.sde_id}
              className={`p-6 bg-wedding-charcoal border cursor-pointer transition-colors ${
                selectedProject?.sde_id === project.sde_id
                  ? "border-wedding-gold/50"
                  : "border-white/5 hover:border-wedding-gold/20"
              }`}
              onClick={() => setSelectedProject(project)}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid={`sde-project-${project.sde_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-serif text-lg text-wedding-ivory">{({"groom":"Groom","bride":"Bride","best_man":"Best Man","maid_of_honor":"Maid of Honor"})[project.sde_type] || project.sde_type}</h3>
                  <p className="text-wedding-gold text-sm">{project.participant_name}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded ${
                  project.status === "published" ? "bg-green-500/20 text-green-400" : "bg-white/10 text-wedding-ivory/50"
                }`}>{project.status}</span>
              </div>
              <div className="flex items-center justify-between text-wedding-ivory/40 text-sm">
                <span>{project.clips?.length || 0} clips</span>
                <span>Transition: {project.transition_type}</span>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-white/5">
                <Button size="sm" onClick={(e) => { e.stopPropagation(); handleTogglePublish(project); }}
                  className="bg-white/5 text-wedding-ivory/60 hover:bg-white/10 border border-white/10 text-xs">
                  {project.status === "published" ? <><EyeOff className="w-3 h-3 mr-1" />Unpublish</> : <><Eye className="w-3 h-3 mr-1" />Publish</>}
                </Button>
                <Button size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(project.sde_id); }}
                  className="bg-red-600/20 text-red-500 hover:bg-red-600/30 border border-red-500/30 text-xs">
                  <Trash2 className="w-3 h-3 mr-1" />Delete
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Clip Manager Panel */}
      {selectedProject && (
        <motion.div className="p-6 bg-wedding-charcoal border border-wedding-gold/20" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-serif text-xl text-wedding-ivory">
                {({"groom":"Groom","bride":"Bride","best_man":"Best Man","maid_of_honor":"Maid of Honor"})[selectedProject.sde_type] || selectedProject.sde_type} — Clips
              </h2>
              <p className="text-wedding-ivory/40 text-sm">{selectedProject.participant_name} | 30 slots, 5s each</p>
            </div>
            <button onClick={() => setSelectedProject(null)} className="text-wedding-ivory/40 hover:text-wedding-ivory">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Clip Preview */}
          {previewClip && (
            <div className="mb-6 relative max-w-md mx-auto">
              <div className="aspect-video bg-wedding-subtle rounded-sm overflow-hidden">
                <video
                  src={getMediaUrl(previewClip.video_url)}
                  className="w-full h-full object-cover"
                  controls
                  autoPlay
                  data-testid="clip-preview-video"
                />
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-wedding-ivory/50 text-sm">Slot {previewClip.slot_number}</span>
                <button onClick={() => setPreviewClip(null)} className="text-wedding-ivory/40 hover:text-wedding-ivory text-sm">
                  Close preview
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-2">
            {Array.from({ length: 30 }, (_, i) => i + 1).map(slot => {
              const clip = selectedProject.clips?.find(c => c.slot_number === slot);
              return (
                <div key={slot} className="relative group">
                  {clip ? (
                    <div
                      className={`aspect-square bg-wedding-gold/20 border rounded-sm flex items-center justify-center relative cursor-pointer transition-colors ${
                        previewClip?.slot_number === slot ? "border-wedding-gold" : "border-wedding-gold/30 hover:border-wedding-gold/60"
                      }`}
                      onClick={() => setPreviewClip(clip)}
                      data-testid={`clip-slot-${slot}`}
                    >
                      <Play className="w-4 h-4 text-wedding-gold" />
                      <span className="absolute top-0.5 left-1 text-[10px] text-wedding-gold">{slot}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteClip(slot); }}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-2.5 h-2.5 text-white" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => triggerUpload(slot)}
                      disabled={uploading}
                      className="aspect-square w-full border border-dashed border-white/15 hover:border-wedding-gold/40 rounded-sm flex items-center justify-center transition-colors"
                      data-testid={`upload-slot-${slot}`}
                    >
                      {uploading && uploadSlot === slot ? (
                        <div className="w-4 h-4 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <span className="text-[10px] text-wedding-ivory/30">{slot}</span>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Create Modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-wedding-charcoal border-white/10">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-wedding-ivory">New SDE Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div>
              <Label className="form-label">Perspective</Label>
              <Select value={newProject.sde_type} onValueChange={(v) => setNewProject({ ...newProject, sde_type: v })}>
                <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wedding-charcoal border-white/10">
                  {SDE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">Participant Name</Label>
              <Input value={newProject.participant_name} onChange={(e) => setNewProject({ ...newProject, participant_name: e.target.value })}
                placeholder="e.g. Jason" className="input-premium mt-2" />
            </div>
            <div>
              <Label className="form-label">Transition</Label>
              <Select value={newProject.transition_type} onValueChange={(v) => setNewProject({ ...newProject, transition_type: v })}>
                <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wedding-charcoal border-white/10">
                  <SelectItem value="fade">Fade</SelectItem>
                  <SelectItem value="cut">Cut</SelectItem>
                  <SelectItem value="morph">Morph</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button onClick={() => setCreateOpen(false)} className="btn-secondary">Cancel</Button>
              <Button onClick={handleCreate} className="btn-primary" disabled={!newProject.participant_name}>Create</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
